/**
 * RL range snapping utilities.
 * Snaps slider values to RL values supplied directly by the API.
 */

/**
 * Find the nearest RL value in a list of valid RLs.
 * Used for snap-to-marks behavior on the RL range slider.
 *
 * @param value - The target value to snap
 * @param validRLs - Array of valid RL values to snap to
 * @returns The nearest RL value, or the value itself if validRLs is empty
 */
const snapToNearest = (value: number, validRLs: number[]): number => {
  if (validRLs.length === 0) return value;

  return validRLs.reduce((nearest, rl) => (Math.abs(rl - value) < Math.abs(nearest - value) ? rl : nearest));
};

/**
 * Snap a range [min, max] to the nearest valid RL values.
 * Ensures both endpoints snap to existing RL values.
 *
 * @param range - The [min, max] range to snap
 * @param validRLs - Array of valid RL values to snap to
 * @returns Snapped range, or original if validRLs is empty
 */
const snapRangeToNearest = (range: [number, number], validRLs: number[]): [number, number] => {
  if (validRLs.length === 0) return range;

  return [snapToNearest(range[0], validRLs), snapToNearest(range[1], validRLs)];
};

export { snapRangeToNearest };
